/**
 * Pure aggregation util for the Template Health hub. Joins the counts from
 * four existing tag-health surfaces — activation validation, docx↔schema
 * audit, clause tag parse, and body-tag control-tag pairing — into a single
 * summary the hub panel renders. Callers own fetching/deriving each input
 * (including running `parseClauseTags` when clauses are loaded); this module
 * never imports components or calls out to other subsystems on its own, so
 * it stays a leaf module with nothing to mock in tests.
 *
 * `countOrphanControlTags` is the single source for the orphan-pairing rule
 * also used by `BodyTagsViewer` (which imports it rather than re-deriving
 * its own copy).
 */

import type { OrgTemplateSchema, TagValidation } from '../types/orgTemplate';
import { auditSchemaTags } from './schemaTagAudit';
import type { ClauseTagParseResult } from './clauseTagParser';

export interface TemplateHealthCounts {
  activationWarnings: number;
  schemaAudit: number;
  clauseLibrary: number;
  bodyTagOrphans: number;
  total: number;
}

export interface AggregateTemplateHealthInput {
  bodyTags: string[] | undefined;
  schema: OrgTemplateSchema | undefined;
  tagValidation: TagValidation | undefined;
  /** Caller runs parseClauseTags when clauses are loaded; undefined means not loaded yet. */
  parse: ClauseTagParseResult | undefined;
  orphanedClauseIds: string[] | undefined;
}

/**
 * Count control tags ('#'/'/'/'^'-prefixed) whose open/close pairing is
 * broken: an open ('#') or inverted ('^') with no matching close ('/'), or a
 * close with no matching open. Mirrors the pairing rule the docxtemplater
 * body-tag viewer uses so both surfaces agree on what "orphaned" means.
 */
export function countOrphanControlTags(bodyTags: string[] | undefined): number {
  if (!bodyTags || bodyTags.length === 0) return 0;

  const grouped = new Map<string, { hasOpen: boolean; hasClose: boolean }>();

  for (const tag of bodyTags) {
    const prefix = tag[0];
    if (prefix !== '#' && prefix !== '/' && prefix !== '^') continue;

    const baseName = tag.slice(1);
    const entry = grouped.get(baseName) ?? { hasOpen: false, hasClose: false };
    if (prefix === '#') entry.hasOpen = true;
    if (prefix === '/') entry.hasClose = true;
    grouped.set(baseName, entry);
  }

  let orphanCount = 0;
  for (const { hasOpen, hasClose } of grouped.values()) {
    if (!(hasOpen && hasClose)) orphanCount += 1;
  }
  return orphanCount;
}

export function aggregateTemplateHealth(
  input: AggregateTemplateHealthInput
): TemplateHealthCounts {
  const { bodyTags, schema, tagValidation, parse, orphanedClauseIds } = input;

  const activationWarnings = tagValidation?.warnings.length ?? 0;

  const schemaAudit =
    bodyTags && bodyTags.length > 0 && schema
      ? (() => {
          const { missing, unknown, unbalanced } = auditSchemaTags(bodyTags, schema);
          return missing.length + unknown.length + unbalanced.length;
        })()
      : 0;

  const clauseLibrary = parse
    ? parse.unknownTags.length + parse.ambiguousTags.length + (orphanedClauseIds?.length ?? 0)
    : 0;

  const bodyTagOrphans = countOrphanControlTags(bodyTags);

  const total = activationWarnings + schemaAudit + clauseLibrary + bodyTagOrphans;

  return { activationWarnings, schemaAudit, clauseLibrary, bodyTagOrphans, total };
}
